import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { TERMS_MD, PRIVACY_MD } from "./legalContent";

/**
 * Sprint J-4-B · Public Terms / Privacy pages.
 *
 * Dark Nexus One theme — uses --atlas-* tokens throughout.
 * Renders TERMS_MD or PRIVACY_MD as light markdown (headings + paragraphs).
 * Includes version + last-updated + material-change notice banner.
 *
 * Routes:
 *   /terms    → doc="terms"
 *   /privacy  → doc="privacy"
 */

const DOC_VERSION = "2026-08-nx1-launch";
const LAST_UPDATED = "August 5, 2026";

const DOCS = {
    terms:   { title: "Terms of Service", body: TERMS_MD,   testid: "public-terms" },
    privacy: { title: "Privacy Policy",   body: PRIVACY_MD, testid: "public-privacy" },
};

export default function PublicLegalPage({ doc }) {
    const { pathname } = useLocation();
    const entry = DOCS[doc] || DOCS.terms;

    useEffect(() => {
        document.title = `${entry.title} · Nexus One`;
        window.scrollTo({ top: 0 });
    }, [entry.title, pathname]);

    // Very light markdown → JSX renderer (headings, paragraphs, lists).
    const sections = useMemo(() => renderMarkdown(entry.body), [entry.body]);

    return (
        <div
            className="min-h-screen"
            style={{ background: "var(--atlas-bg-deep)", color: "var(--atlas-text-primary)" }}
            data-testid={entry.testid}
        >
            <header
                className="border-b"
                style={{
                    borderColor: "var(--atlas-border-soft)",
                    background: "var(--atlas-bg-elevated)",
                }}
            >
                <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 text-sm"
                        style={{ color: "var(--atlas-text-secondary)" }}
                    >
                        <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Nexus One
                    </Link>
                    <div className="flex gap-5 text-[13px]">
                        <Link
                            to="/terms"
                            style={{
                                color: doc === "terms" ? "var(--atlas-primary-hover)" : "var(--atlas-text-tertiary)",
                                fontWeight: doc === "terms" ? 500 : 400,
                            }}
                            data-testid="nav-terms"
                        >
                            Terms
                        </Link>
                        <Link
                            to="/privacy"
                            style={{
                                color: doc === "privacy" ? "var(--atlas-primary-hover)" : "var(--atlas-text-tertiary)",
                                fontWeight: doc === "privacy" ? 500 : 400,
                            }}
                            data-testid="nav-privacy"
                        >
                            Privacy
                        </Link>
                        <Link
                            to="/legal/dpa"
                            style={{ color: "var(--atlas-text-tertiary)" }}
                        >
                            DPA
                        </Link>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10">
                <div className="wfos-label" style={{ color: "var(--atlas-text-tertiary)" }}>
                    Nexus One · Legal
                </div>
                <h1 className="text-3xl font-semibold mt-1" style={{ color: "var(--atlas-text-primary)" }}>
                    {entry.title}
                </h1>
                <div className="mt-2 flex items-center gap-3 text-[12px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                    <span>Version {DOC_VERSION}</span>
                    <span>·</span>
                    <span>Last updated {LAST_UPDATED}</span>
                </div>

                <div
                    className="mt-6 mb-8 flex items-start gap-3 rounded-lg p-4"
                    style={{
                        background: "rgba(139, 92, 246, 0.08)",
                        border: "1px solid rgba(139, 92, 246, 0.30)",
                    }}
                >
                    <ShieldCheck
                        className="w-5 h-5 flex-shrink-0 mt-0.5"
                        style={{ color: "var(--atlas-primary-hover)" }}
                        strokeWidth={1.75}
                    />
                    <div className="text-[13px] leading-relaxed" style={{ color: "var(--atlas-text-secondary)" }}>
                        <b style={{ color: "var(--atlas-text-primary)" }}>Material changes trigger notice + re-acknowledgment.</b>{" "}
                        We'll email account admins at least 30 days before any material change takes effect and require an
                        in-product acknowledgment before continued use. See the change log at the bottom of this page.
                    </div>
                </div>

                <article className="prose-nexus space-y-4" data-testid={`${entry.testid}-body`}>
                    {sections}
                </article>

                <footer
                    className="mt-14 pt-6 border-t text-[12px]"
                    style={{ borderColor: "var(--atlas-border-soft)", color: "var(--atlas-text-tertiary)" }}
                >
                    <div>
                        Questions? <a
                            href="mailto:legal@nexusone.ai"
                            style={{ color: "var(--atlas-primary-hover)" }}
                        >legal@nexusone.ai</a>
                    </div>
                    <div className="mt-1">
                        © Nexus One — Matcom Operations. All rights reserved.
                    </div>
                </footer>
            </main>
        </div>
    );
}

/**
 * Minimal markdown → JSX renderer scoped to the format used in
 * legalContent.js: `##` headings, blank-line paragraphs, `-` bullet lists.
 * Handles the common pattern of a `## Heading\nParagraph body...\n\n` block
 * where the heading and the paragraph share one block (single `\n` between).
 */
function renderMarkdown(md) {
    const blocks = md.split(/\n\n+/).map((b) => b.trim()).filter(Boolean);
    const out = [];
    let key = 0;
    for (const block of blocks) {
        // Peel off a leading `## ` heading if present, render remainder below.
        let rest = block;
        if (rest.startsWith("## ")) {
            const nl = rest.indexOf("\n");
            const headingText = (nl === -1 ? rest.slice(3) : rest.slice(3, nl)).trim();
            out.push(
                <h2
                    key={key++}
                    className="text-lg font-semibold mt-8"
                    style={{ color: "var(--atlas-text-primary)" }}
                >
                    {headingText}
                </h2>
            );
            rest = nl === -1 ? "" : rest.slice(nl + 1).trim();
            if (!rest) continue;
        }
        // Bullet list block.
        if (rest.startsWith("- ")) {
            const items = rest.split("\n").filter((l) => l.trim().startsWith("- ")).map((l) => l.trim().slice(2).trim());
            out.push(
                <ul
                    key={key++}
                    className="list-disc pl-6 space-y-1 text-[14px] leading-relaxed"
                    style={{ color: "var(--atlas-text-secondary)" }}
                >
                    {items.map((it, j) => (
                        <li key={j}>{it}</li>
                    ))}
                </ul>
            );
            continue;
        }
        // Mixed paragraph + inline bullets: split by newline, group `- ` lines.
        const lines = rest.split("\n");
        const buffer = [];
        let bullets = [];
        const flushBullets = () => {
            if (!bullets.length) return;
            out.push(
                <ul
                    key={key++}
                    className="list-disc pl-6 space-y-1 text-[14px] leading-relaxed"
                    style={{ color: "var(--atlas-text-secondary)" }}
                >
                    {bullets.map((it, j) => (
                        <li key={j}>{it}</li>
                    ))}
                </ul>
            );
            bullets = [];
        };
        const flushBuffer = () => {
            if (!buffer.length) return;
            const text = buffer.join(" ").trim();
            if (text) {
                out.push(
                    <p
                        key={key++}
                        className="text-[14px] leading-relaxed"
                        style={{ color: "var(--atlas-text-secondary)" }}
                    >
                        {text}
                    </p>
                );
            }
            buffer.length = 0;
        };
        for (const ln of lines) {
            const t = ln.trim();
            if (t.startsWith("- ")) {
                flushBuffer();
                bullets.push(t.slice(2).trim());
            } else if (!t) {
                flushBullets();
                flushBuffer();
            } else {
                flushBullets();
                buffer.push(t);
            }
        }
        flushBullets();
        flushBuffer();
    }
    return out;
}
